<template>
  <div class="checkin-root">
    <!-- 헤더 -->
    <div class="checkin-header">
      <button class="back-btn" @click="$router.back()">←</button>
      <div class="header-center">
        <span class="header-title">입장 확인</span>
        <span v-if="liveTitle" class="header-subtitle">{{ liveTitle }}</span>
      </div>
      <div class="header-stats">
        <span class="stats-badge">{{ checkedInCount }}/{{ admissibleCount }}명 입장</span>
      </div>
    </div>

    <!-- 카메라 영역 -->
    <div class="camera-area">
      <div v-if="!cameraStarted" class="camera-placeholder">
        <button class="start-camera-btn" @click="startScan" :disabled="loading">
          <span class="camera-icon">📷</span>
          <span>카메라 시작</span>
        </button>
        <div v-if="cameraError" class="camera-error-msg">{{ cameraError }}</div>
      </div>

      <div v-else class="camera-live">
        <video ref="qrVideo" class="qr-video" autoplay playsinline muted></video>
        <div class="scan-overlay">
          <div class="scan-frame"></div>
          <div class="scan-line" :class="{ scanning: isScanning }"></div>
        </div>
        <div class="camera-controls">
          <button class="ctrl-btn" @click="toggleFacing" title="카메라 전환">
            🔄 {{ facingMode === 'user' ? '후면' : '전면' }} 전환
          </button>
          <button class="ctrl-btn danger" @click="stopScan">중지</button>
        </div>
        <div v-if="cameraError" class="camera-error-msg overlay-error">{{ cameraError }}</div>
      </div>
    </div>

    <!-- 결과 패널 (QR 인식 후) -->
    <transition name="slide-up">
      <div v-if="result" class="result-panel" :class="resultPanelClass">
        <!-- 상태 배지 -->
        <div class="result-status-row">
          <span class="result-status-badge" :class="resultStatusClass">
            {{ resultStatusText }}
          </span>
          <button class="result-close-btn" @click="dismissResult">✕</button>
        </div>

        <!-- 예약자 정보 -->
        <div class="result-info">
          <div class="result-name-row">
            <span class="result-name">{{ result.name }}</span>
            <span v-if="result.isInviteTicket" class="invite-badge">🎟️ 초대권</span>
            <span class="result-ticket">#{{ getTicketNo(result.id) }}</span>
          </div>
          <div class="result-qty">티켓 {{ result.quantity || 0 }}매</div>

          <div v-if="result._rewardSummary && result._rewardSummary.length" class="result-extra">
            <div class="extra-title">티켓 패키지</div>
            <div v-for="(rw, i) in result._rewardSummary" :key="i" class="extra-row">
              <span>{{ rw.name }}</span>
              <span>{{ rw.quantity }}개</span>
            </div>
          </div>
          <div v-if="result._purchaseSummary && result._purchaseSummary.length" class="result-extra">
            <div class="extra-title">추가구매</div>
            <div v-for="(g, i) in result._purchaseSummary" :key="i" class="extra-row">
              <span>{{ g.name }}<span v-if="g.optionAnswer" class="option-text"> ({{ g.optionAnswer }})</span></span>
              <span>{{ g.quantity }}개</span>
            </div>
          </div>
        </div>

        <!-- 이미 입장처리된 경우 경고 -->
        <div v-if="result.checkedIn && !justCheckedIn" class="already-checked-warning">
          ⚠️ 이미 입장 처리된 티켓입니다.
        </div>

        <!-- 성공 메시지 -->
        <div v-if="justCheckedIn" class="just-checked-msg">
          ✓ 입장 처리 완료
        </div>

        <!-- 액션 버튼 -->
        <div v-if="resultIsAdmissible" class="result-actions">
          <button
            v-if="!result.checkedIn"
            class="btn-checkin"
            :disabled="checkingIn"
            @click="doCheckIn"
          >
            {{ checkingIn ? '처리 중...' : '입장 확인' }}
          </button>
          <button
            v-else
            class="btn-undo"
            :disabled="checkingIn"
            @click="doUndoCheckIn"
          >
            {{ checkingIn ? '처리 중...' : '입장 취소' }}
          </button>
        </div>
        <div v-else class="reject-reason">{{ rejectReason }}</div>

        <!-- 자동 넘어감 진행바 -->
        <div v-if="autoNextTotal > 0" class="auto-next-bar-wrap" @click="nextScan">
          <div class="auto-next-label">탭하면 바로 스캔 →</div>
          <div class="auto-next-bar">
            <div class="auto-next-fill" :style="{ width: (autoNextProgress / autoNextTotal * 100) + '%' }"></div>
          </div>
        </div>
      </div>
    </transition>

    <!-- 로딩 중 -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">로딩 중...</div>
    </div>
  </div>
</template>

<script>
import jsQR from "jsqr";
import { useUserStore } from "@/store/user";
import { getTicketReservationsByLiveId } from "@/utils/read/ticket";
import { setReservationCheckIn } from "@/utils/write/ticket";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { firestore } from "../../../firebaseConfig";

export default {
  name: "CheckInView",
  data() {
    return {
      userStore: useUserStore(),
      loading: true,
      liveTitle: "",
      formInfo: null,
      reservations: [],
      isTicketType: true,

      cameraStarted: false,
      isScanning: false,
      facingMode: "environment",
      stream: null,
      detector: null,
      cameraError: "",

      result: null,
      justCheckedIn: false,
      checkingIn: false,
      autoNextTimer: null,
      autoNextProgress: 0,
      autoNextTotal: 0,
    };
  },
  computed: {
    entityId() {
      return this.$route.params.id;
    },
    type() {
      return this.$route.query.type || "ticket";
    },
    admissibleCount() {
      return this.reservations.filter((r) => this.canCheckIn(r)).length;
    },
    checkedInCount() {
      return this.reservations.filter((r) => r.checkedIn && this.canCheckIn(r)).length;
    },
    resultIsAdmissible() {
      if (!this.result) return false;
      return this.canCheckIn(this.result);
    },
    resultStatusText() {
      if (!this.result) return "";
      if (!this.resultIsAdmissible) return "✕ 입장 불가";
      if (this.result.checkedIn) return "⚠️ 재입장";
      return "✓ 입장 가능";
    },
    resultStatusClass() {
      if (!this.result) return "";
      if (!this.resultIsAdmissible) return "badge-reject";
      if (this.result.checkedIn) return "badge-warn";
      return "badge-ok";
    },
    resultPanelClass() {
      if (!this.result) return "";
      if (!this.resultIsAdmissible) return "panel-reject";
      if (this.result.checkedIn) return "panel-warn";
      return "panel-ok";
    },
    rejectReason() {
      if (!this.result) return "";
      const map = {
        cancelled: "취소된 예매입니다.",
        refund_requested: "환불 요청 중인 예매입니다.",
        refund_completed: "환불 완료된 예매입니다.",
        pending: "아직 확정되지 않은 예매입니다.",
      };
      return map[this.result.status] || `상태: ${this.result.status}`;
    },
  },
  async mounted() {
    if (!this.userStore.isLogin) {
      this.$alert("로그인이 필요합니다.");
      this.$router.push("/");
      return;
    }
    this.isTicketType = this.type !== "commission";
    await this.loadData();
    this.loading = false;
  },
  beforeUnmount() {
    this.stopScan();
    this.clearAutoNext();
  },
  methods: {
    // ── 데이터 로드 ──────────────────────────────
    async loadData() {
      try {
        if (this.isTicketType) {
          // 라이브 정보
          try {
            const liveDoc = await getDoc(doc(firestore, "lives", this.entityId));
            if (liveDoc.exists()) {
              this.liveTitle = liveDoc.data()?.title || "";
            }
          } catch (e) { console.warn("라이브 정보 로드 실패:", e); }

          // 티켓 폼
          try {
            const formDoc = await getDoc(doc(firestore, "ticketForm", this.entityId));
            if (formDoc.exists()) {
              this.formInfo = formDoc.data();
            }
          } catch (e) { console.warn("티켓폼 로드 실패:", e); }

          // 예매 목록 (서버 API)
          try {
            this.reservations = await getTicketReservationsByLiveId(this.entityId);
          } catch (e) {
            console.error("예매 목록 로드 실패:", e);
            this.cameraError = "예매 목록을 불러오지 못했습니다.";
          }
        } else {
          // 커미션
          try {
            const commDoc = await getDoc(doc(firestore, "commissions", this.entityId));
            if (commDoc.exists()) {
              this.liveTitle = commDoc.data()?.title || "";
            }
          } catch (e) { console.warn("커미션 정보 로드 실패:", e); }

          try {
            const formDoc = await getDoc(doc(firestore, "purchaseForm", this.entityId));
            if (formDoc.exists()) {
              this.formInfo = formDoc.data();
            }
          } catch (e) { console.warn("커미션 폼 로드 실패:", e); }

          try {
            const resSnap = await getDocs(
              query(collection(firestore, "purchaseReservations"), where("liveId", "==", this.entityId))
            );
            this.reservations = resSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          } catch (e) {
            console.error("커미션 구매 목록 로드 실패:", e);
            this.cameraError = "구매 목록을 불러오지 못했습니다.";
          }
        }
      } catch (err) {
        console.error("CheckInView 데이터 로드 오류:", err);
      }
    },

    // ── 유틸 ──────────────────────────────────────
    getTicketNo(id) {
      return String(id || "").slice(-8).toUpperCase();
    },
    /** 일반 예매: 확정(confirmed)만 입장. 초대권: 취소만 아니면 입장 (예매관리 리스트와 동일) */
    canCheckIn(r) {
      if (!r) return false;
      if (r.isInviteTicket) return r.status !== "cancelled";
      return r.status === "confirmed";
    },
    /**
     * 스캔 결과 피드백음 (Web Audio). 별도 파일 없이 짧은 비프음.
     * eligible: 입장 가능(확정·미입장) / alreadyIn: 확정이나 이미 입장 / ineligible: 미확정·취소·환불 등 / error: 인식 실패·API 실패
     */
    playFeedbackSound(kind) {
      if (typeof window === "undefined") return;
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        const run = () => {
          const t0 = ctx.currentTime;
          switch (kind) {
            case "eligible": {
              // 밝은 3화음 체크인 효과음 (사인파로 깔끔하게)
              const chord = (freq, off, dur) => {
                const osc2 = ctx.createOscillator();
                const g2 = ctx.createGain();
                osc2.connect(g2);
                g2.connect(ctx.destination);
                osc2.type = "sine";
                osc2.frequency.setValueAtTime(freq, t0 + off);
                g2.gain.setValueAtTime(0.0001, t0 + off);
                g2.gain.exponentialRampToValueAtTime(0.45, t0 + off + 0.015);
                g2.gain.setValueAtTime(0.45, t0 + off + dur * 0.6);
                g2.gain.exponentialRampToValueAtTime(0.0001, t0 + off + dur);
                osc2.start(t0 + off);
                osc2.stop(t0 + off + dur + 0.04);
              };
              // Do–Mi–Sol 3화음 아르페지오
              chord(523, 0, 0.18);      // C5
              chord(659, 0.1, 0.2);     // E5
              chord(784, 0.2, 0.28);    // G5
              // 마지막에 옥타브 위 C로 마무리
              chord(1047, 0.32, 0.32);  // C6
              break;
            }
            case "alreadyIn": {
              // 이미 입장 — 따뜻한 하강 2음 (경고지만 부드럽게)
              const warm = (freq, off, dur, vol = 0.42) => {
                const osc2 = ctx.createOscillator();
                const g2 = ctx.createGain();
                osc2.connect(g2);
                g2.connect(ctx.destination);
                osc2.type = "triangle";
                osc2.frequency.setValueAtTime(freq, t0 + off);
                g2.gain.setValueAtTime(0.0001, t0 + off);
                g2.gain.exponentialRampToValueAtTime(vol, t0 + off + 0.018);
                g2.gain.setValueAtTime(vol, t0 + off + dur * 0.65);
                g2.gain.exponentialRampToValueAtTime(0.0001, t0 + off + dur);
                osc2.start(t0 + off);
                osc2.stop(t0 + off + dur + 0.04);
              };
              // Mi–Do 하강 (G4 → E4)
              warm(659, 0, 0.22);     // E5
              warm(523, 0.28, 0.30);  // C5
              break;
            }
            case "ineligible": {
              // 입장 불가 — 단조 하강 3음 (명확하지만 거슬리지 않게)
              const minor = (freq, off, dur, vol = 0.45) => {
                const osc2 = ctx.createOscillator();
                const filt2 = ctx.createBiquadFilter();
                const g2 = ctx.createGain();
                osc2.connect(filt2);
                filt2.connect(g2);
                g2.connect(ctx.destination);
                osc2.type = "triangle";
                filt2.type = "lowpass";
                filt2.frequency.value = 1200;
                osc2.frequency.setValueAtTime(freq, t0 + off);
                g2.gain.setValueAtTime(0.0001, t0 + off);
                g2.gain.exponentialRampToValueAtTime(vol, t0 + off + 0.018);
                g2.gain.setValueAtTime(vol, t0 + off + dur * 0.6);
                g2.gain.exponentialRampToValueAtTime(0.0001, t0 + off + dur);
                osc2.start(t0 + off);
                osc2.stop(t0 + off + dur + 0.04);
              };
              // A4 → F4 → D4 단조 하강
              minor(440, 0, 0.22);    // A4
              minor(349, 0.26, 0.22); // F4
              minor(294, 0.52, 0.28); // D4
              break;
            }
            case "error": {
              // 오류 — 짧은 저음 2연타 (귀에 익숙한 "에러" 느낌)
              const buzz = (freq, off, dur, vol = 0.5) => {
                const osc2 = ctx.createOscillator();
                const filt2 = ctx.createBiquadFilter();
                const g2 = ctx.createGain();
                osc2.connect(filt2);
                filt2.connect(g2);
                g2.connect(ctx.destination);
                osc2.type = "sawtooth";
                filt2.type = "lowpass";
                filt2.frequency.value = 600;
                osc2.frequency.setValueAtTime(freq, t0 + off);
                g2.gain.setValueAtTime(0.0001, t0 + off);
                g2.gain.exponentialRampToValueAtTime(vol, t0 + off + 0.015);
                g2.gain.setValueAtTime(vol, t0 + off + dur * 0.5);
                g2.gain.exponentialRampToValueAtTime(0.0001, t0 + off + dur);
                osc2.start(t0 + off);
                osc2.stop(t0 + off + dur + 0.04);
              };
              buzz(220, 0, 0.22);
              buzz(185, 0.28, 0.30);
              break;
            }
            default:
              break;
          }
          setTimeout(() => {
            try {
              ctx.close();
            } catch (_) {
              /* ignore */
            }
          }, 900);
        };
        if (ctx.state === "suspended") {
          ctx.resume().then(run).catch(() => {});
        } else {
          run();
        }
      } catch (_) {
        /* 오디오 미지원·자동재생 제한 시 무시 */
      }
    },
    getRewardSummary(reservation) {
      const tiers = this.formInfo?.rewardTiers;
      const answers = reservation?.rewardAnswers;
      if (!Array.isArray(tiers) || !tiers.length || !Array.isArray(answers)) return [];
      return answers
        .map((a) => {
          const tier = tiers[a.tierIndex];
          if (!tier || !a.quantity) return null;
          return { name: tier.name, quantity: a.quantity };
        })
        .filter(Boolean);
    },
    getPurchaseSummary(reservation) {
      const items = this.formInfo?.purchaseItems;
      const answers = reservation?.purchaseAnswers;
      if (!Array.isArray(items) || !items.length || !Array.isArray(answers)) return [];
      return items
        .map((item, i) => {
          const ans = answers[i];
          const qty = ans?.quantity || 0;
          if (!qty) return null;
          let optionAnswer = null;
          if (ans?.optionAnswer) {
            optionAnswer = Array.isArray(ans.optionAnswer)
              ? ans.optionAnswer.join(", ")
              : String(ans.optionAnswer);
          }
          return { name: item.name || `추가구매${i + 1}`, quantity: qty, optionAnswer };
        })
        .filter(Boolean);
    },
    parseQrPayload(text) {
      const raw = String(text || "").trim();
      if (!raw) return { reservationId: null, ticketNo: null };

      if (raw.startsWith("http")) {
        try {
          const u = new URL(raw);
          const rid = u.searchParams.get("rid");
          return { reservationId: rid, ticketNo: null };
        } catch (e) { /* fallthrough */ }
      }

      if (raw.startsWith("{") && raw.endsWith("}")) {
        try {
          const obj = JSON.parse(raw);
          return {
            reservationId: obj.reservationId || obj.rid || null,
            ticketNo: obj.ticketNo || null,
          };
        } catch (e) { /* fallthrough */ }
      }

      const normalized = raw.replace(/^#/, "").trim().toUpperCase();
      if (/^[A-Z0-9]{8}$/.test(normalized)) {
        return { reservationId: null, ticketNo: normalized };
      }

      return { reservationId: raw, ticketNo: null };
    },

    // ── 카메라 ────────────────────────────────────
    async startScan() {
      this.cameraError = "";
      try {
        if (typeof window !== "undefined" && "BarcodeDetector" in window) {
          this.detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        }
      } catch (e) { /* ignore */ }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: this.facingMode } },
          audio: false,
        });
        this.stream = stream;
        this.cameraStarted = true;
        this.isScanning = true;

        await this.$nextTick();
        const video = this.$refs.qrVideo;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        this.runScanLoop(video);
      } catch (e) {
        this.cameraError = "카메라 권한 또는 HTTPS 접속이 필요합니다.";
      }
    },
    runScanLoop(video) {
      const loop = async () => {
        if (!this.isScanning) return;
        try {
          let raw = "";
          if (this.detector) {
            const codes = await this.detector.detect(video);
            if (codes && codes.length) raw = codes[0].rawValue || "";
          } else {
            raw = this.detectWithCanvas(video);
          }
          if (raw) {
            this.onQrDecoded(raw);
            return;
          }
        } catch (e) {
          try {
            const raw = this.detectWithCanvas(video);
            if (raw) { this.onQrDecoded(raw); return; }
          } catch (_) { /* ignore */ }
        }
        requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);
    },
    detectWithCanvas(video) {
      const w = video?.videoWidth || 0;
      const h = video?.videoHeight || 0;
      if (!w || !h) return "";
      if (!this.$options._qrCanvas) this.$options._qrCanvas = document.createElement("canvas");
      const cvs = this.$options._qrCanvas;
      cvs.width = w;
      cvs.height = h;
      const ctx = cvs.getContext("2d", { willReadFrequently: true });
      if (!ctx) return "";
      ctx.drawImage(video, 0, 0, w, h);
      const imageData = ctx.getImageData(0, 0, w, h);
      const code = jsQR(imageData.data, w, h);
      return code?.data || "";
    },
    stopScan() {
      this.isScanning = false;
      try {
        const video = this.$refs.qrVideo;
        if (video) video.srcObject = null;
      } catch (e) { /* ignore */ }
      if (this.stream) {
        this.stream.getTracks().forEach((t) => t.stop());
        this.stream = null;
      }
      this.detector = null;
      this.cameraStarted = false;
    },
    async toggleFacing() {
      const wasScanning = this.isScanning;
      if (wasScanning) this.stopScan();
      this.facingMode = this.facingMode === "environment" ? "user" : "environment";
      if (wasScanning) {
        await this.$nextTick();
        await this.startScan();
      }
    },

    // ── QR 처리 ───────────────────────────────────
    onQrDecoded(text) {
      this.isScanning = false;
      this.cameraError = "";

      const { reservationId, ticketNo } = this.parseQrPayload(text);
      if (!reservationId && !ticketNo) {
        this.playFeedbackSound("error");
        this.cameraError = "QR 내용을 해석할 수 없습니다.";
        this.isScanning = true;
        this.runScanLoop(this.$refs.qrVideo);
        return;
      }

      let target = null;
      if (reservationId) {
        target = this.reservations.find((r) => r.id === reservationId) || null;
      }
      if (!target && ticketNo) {
        const t = ticketNo.replace(/^#/, "").toUpperCase();
        target = this.reservations.find((r) => this.getTicketNo(r.id) === t) || null;
      }
      if (!target) {
        this.playFeedbackSound("error");
        this.cameraError = "이 공연의 예매 내역에서 찾을 수 없습니다.";
        this.isScanning = true;
        this.runScanLoop(this.$refs.qrVideo);
        return;
      }

      this.justCheckedIn = false;
      this.result = {
        ...target,
        _rewardSummary: this.getRewardSummary(target),
        _purchaseSummary: this.getPurchaseSummary(target),
      };

      if (this.canCheckIn(target) && !target.checkedIn) {
        this.playFeedbackSound("eligible");
        // 입장 가능 + 미입장 → 즉시 체크인 (doCheckIn 내부에서 타이머 예약)
        this.doCheckIn();
      } else if (this.canCheckIn(target) && target.checkedIn) {
        this.playFeedbackSound("alreadyIn");
        // 이미 입장한 티켓 → 3초 후 자동 넘어감
        this.scheduleAutoNext(3000);
      } else {
        this.playFeedbackSound("ineligible");
        // 입장 불가(미확정·취소·환불 등) → 3초 후 자동 넘어감
        this.scheduleAutoNext(3000);
      }
    },

    async doCheckIn() {
      if (!this.result || this.checkingIn) return;
      this.clearAutoNext();
      this.checkingIn = true;
      const targetId = this.result.id;
      console.log("[CheckIn] 입장처리 요청:", { id: targetId, type: this.type });
      try {
        if (this.isTicketType) {
          const result = await setReservationCheckIn(targetId, true);
          console.log("[CheckIn] 서버 응답:", result);
          // 서버가 200을 주지만 success:false 같은 응답을 줄 수 있어 명시적으로 체크
          if (result && result.success === false) {
            throw new Error(result.message || "서버에서 처리 실패 응답");
          }
        } else {
          await updateDoc(doc(firestore, "purchaseReservations", targetId), {
            checkedIn: true,
            checkedInAt: serverTimestamp(),
          });
        }
        this.reservations = this.reservations.map((r) =>
          r.id === targetId ? { ...r, checkedIn: true } : r
        );
        this.result = { ...this.result, checkedIn: true };
        this.justCheckedIn = true;
        this.scheduleAutoNext(1800);
      } catch (e) {
        console.error("[CheckIn] 입장 처리 실패:", e);
        this.playFeedbackSound("error");
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "알 수 없는 오류";
        this.justCheckedIn = false;
        this.cameraError = `입장 처리 실패: ${msg}`;
        this.$alert(`입장 처리에 실패했습니다.\n(${msg})\n\nID: ${targetId}`);
        this.scheduleAutoNext(4000);
      } finally {
        this.checkingIn = false;
      }
    },
    async doUndoCheckIn() {
      if (!this.result || this.checkingIn) return;
      this.clearAutoNext();
      this.checkingIn = true;
      const targetId = this.result.id;
      try {
        if (this.isTicketType) {
          const result = await setReservationCheckIn(targetId, false);
          if (result && result.success === false) {
            throw new Error(result.message || "서버에서 처리 실패 응답");
          }
        } else {
          await updateDoc(doc(firestore, "purchaseReservations", targetId), {
            checkedIn: false,
            checkedInAt: null,
          });
        }
        this.reservations = this.reservations.map((r) =>
          r.id === targetId ? { ...r, checkedIn: false } : r
        );
        this.result = { ...this.result, checkedIn: false };
        this.justCheckedIn = false;
        this.scheduleAutoNext(2500);
      } catch (e) {
        console.error("[CheckIn] 입장 취소 실패:", e);
        const msg = e?.response?.data?.message || e?.message || "알 수 없는 오류";
        this.$alert(`입장 취소에 실패했습니다.\n(${msg})`);
      } finally {
        this.checkingIn = false;
      }
    },
    dismissResult() {
      this.clearAutoNext();
      this.result = null;
      this.justCheckedIn = false;
      this.nextScan();
    },
    nextScan() {
      this.clearAutoNext();
      this.result = null;
      this.justCheckedIn = false;
      this.cameraError = "";
      if (this.cameraStarted) {
        this.isScanning = true;
        this.$nextTick(() => {
          const video = this.$refs.qrVideo;
          if (video) this.runScanLoop(video);
        });
      } else {
        this.startScan();
      }
    },
    // 자동 다음 스캔 타이머
    scheduleAutoNext(ms) {
      this.clearAutoNext();
      this.autoNextTotal = ms;
      this.autoNextProgress = 0;
      const step = 30;
      const tick = () => {
        this.autoNextProgress += step;
        if (this.autoNextProgress >= this.autoNextTotal) {
          this.autoNextProgress = this.autoNextTotal;
          this.nextScan();
          return;
        }
        this.autoNextTimer = setTimeout(tick, step);
      };
      this.autoNextTimer = setTimeout(tick, step);
    },
    clearAutoNext() {
      if (this.autoNextTimer) {
        clearTimeout(this.autoNextTimer);
        this.autoNextTimer = null;
      }
      this.autoNextProgress = 0;
      this.autoNextTotal = 0;
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.checkin-root {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  background: #0a0a0a;
  color: #fff;
  font-family: "NanumSquareRoundB", sans-serif;
  overflow: hidden;
}

/* ── 헤더 ─────────────────────────────── */
.checkin-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #111;
  border-bottom: 1px solid #222;
  flex-shrink: 0;
  z-index: 10;
}

.back-btn {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 22px;
  cursor: pointer;
  padding: 4px 8px;
  flex-shrink: 0;
}

.header-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}

.header-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
}

.header-subtitle {
  font-size: 12px;
  color: #888;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-stats {
  flex-shrink: 0;
}

.stats-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(102, 189, 255, 0.15);
  color: #66bdff;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid rgba(102, 189, 255, 0.3);
}

/* ── 카메라 영역 ──────────────────────── */
.camera-area {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  overflow: hidden;
  min-height: 0;
}

.camera-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.start-camera-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 40px;
  border-radius: 18px;
  border: 2px dashed rgba(102, 189, 255, 0.4);
  background: rgba(102, 189, 255, 0.06);
  color: #66bdff;
  font-size: 15px;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}

.start-camera-btn:hover {
  border-color: rgba(102, 189, 255, 0.7);
  background: rgba(102, 189, 255, 0.12);
}

.camera-icon {
  font-size: 40px;
}

.camera-live {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scan-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.scan-frame {
  width: min(260px, 65vw);
  height: min(260px, 65vw);
  border: 2px solid rgba(102, 189, 255, 0.7);
  border-radius: 16px;
  box-shadow: 0 0 0 2000px rgba(0, 0, 0, 0.45);
  position: relative;
}

.scan-line {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(240px, 60vw);
  height: 2px;
  background: linear-gradient(90deg, transparent, #66bdff, transparent);
  opacity: 0;
}

.scan-line.scanning {
  opacity: 1;
  animation: scanAnim 1.6s ease-in-out infinite;
}

@keyframes scanAnim {
  0% { transform: translate(-50%, calc(-50% - min(120px, 30vw))); opacity: 0.8; }
  50% { opacity: 1; }
  100% { transform: translate(-50%, calc(-50% + min(120px, 30vw))); opacity: 0.8; }
}

.camera-controls {
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 10px;
}

.ctrl-btn {
  padding: 8px 18px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 13px;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
}

.ctrl-btn.danger {
  border-color: rgba(255, 80, 80, 0.4);
  color: #ff8080;
}

.camera-error-msg {
  color: #ff6b6b;
  font-size: 13px;
  text-align: center;
  max-width: 260px;
}

.overlay-error {
  position: absolute;
  bottom: 56px;
  left: 0;
  right: 0;
  text-align: center;
  padding: 6px 16px;
  background: rgba(0,0,0,0.7);
}

/* ── 결과 패널 ───────────────────────── */
.result-panel {
  flex-shrink: 0;
  padding: 18px 20px 24px;
  border-radius: 24px 24px 0 0;
  max-height: 55dvh;
  overflow-y: auto;
  position: relative;
}

.panel-ok {
  background: #0d2a0d;
  border-top: 2px solid #2e7d32;
}

.panel-warn {
  background: #2a1e00;
  border-top: 2px solid #f57f17;
}

.panel-reject {
  background: #2a0a0a;
  border-top: 2px solid #c62828;
}

.result-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.result-status-badge {
  display: inline-block;
  padding: 5px 16px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
}

.badge-ok {
  background: rgba(46, 125, 50, 0.25);
  color: #69f073;
  border: 1px solid rgba(46, 125, 50, 0.5);
}

.badge-warn {
  background: rgba(245, 127, 23, 0.2);
  color: #ffcc80;
  border: 1px solid rgba(245, 127, 23, 0.4);
}

.badge-reject {
  background: rgba(198, 40, 40, 0.2);
  color: #ef9a9a;
  border: 1px solid rgba(198, 40, 40, 0.4);
}

.result-close-btn {
  background: transparent;
  border: none;
  color: #888;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
}

.result-info {
  margin-bottom: 14px;
}

.result-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.result-name {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
}

.invite-badge {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(124, 58, 237, 0.2);
  color: #c4b5fd;
  border: 1px solid rgba(124, 58, 237, 0.3);
}

.result-ticket {
  font-size: 13px;
  color: #888;
  font-family: monospace;
}

.result-qty {
  font-size: 14px;
  color: #aaa;
  margin-bottom: 10px;
}

.result-extra {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255,255,255,0.08);
}

.extra-title {
  font-size: 11px;
  color: #888;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.extra-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #ccc;
  padding: 2px 0;
}

.option-text {
  color: #888;
}

.already-checked-warning {
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(245, 127, 23, 0.12);
  border: 1px solid rgba(245, 127, 23, 0.3);
  color: #ffcc80;
  font-size: 13px;
}

.just-checked-msg {
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(46, 125, 50, 0.15);
  border: 1px solid rgba(46, 125, 50, 0.3);
  color: #a5d6a7;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
}

.result-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}

.btn-checkin {
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  border: none;
  background: #2e7d32;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-checkin:hover {
  background: #388e3c;
}

.btn-checkin:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-undo {
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.07);
  color: #aaa;
  font-size: 14px;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
}

.btn-undo:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.reject-reason {
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(198, 40, 40, 0.1);
  border: 1px solid rgba(198, 40, 40, 0.25);
  color: #ef9a9a;
  font-size: 13px;
}

.auto-next-bar-wrap {
  margin-top: 14px;
  cursor: pointer;
}

.auto-next-label {
  font-size: 12px;
  color: #666;
  text-align: center;
  margin-bottom: 6px;
}

.auto-next-bar {
  width: 100%;
  height: 4px;
  background: rgba(255,255,255,0.1);
  border-radius: 4px;
  overflow: hidden;
}

.auto-next-fill {
  height: 100%;
  background: rgba(102, 189, 255, 0.6);
  border-radius: 4px;
  transition: width 30ms linear;
}

/* ── 로딩 ─────────────────────────────── */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loading-spinner {
  color: #fff;
  font-size: 16px;
}

/* ── 슬라이드 트랜지션 ─────────────────── */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
